import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import {
  useCreateSearchCandidateProfileSelectedMutation,
  useGetSearchCandidateProfileSelectedQuery,
  useCreateSearchCandidateProfileSelectedItemMutation,
  useDeleteSearchCandidateProfileSelectedItemMutation,
  useGetSearchCandidateProfileSelectedItemsQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { runCoalesced } from 'core/utils/runCoalesced';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { TableV3Ref } from 'common/table';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';

const SESSION_ID_PARAM = 'sessionId';

type SelectedItem = {
  candidateProfileId: string;
  selectionId: string;
};

type UseSearchCandidateProfileSelectedProps = {
  sessionId?: string | null;
  tableRef?: React.RefObject<TableV3Ref<CandidateProfileTableColumns>>;
};

/**
 * Hook to manage candidate profile selection tracking.
 *
 * - If there's a session ID in the URL, it loads and updates that session
 * - If no session ID exists, it creates a new session and adds the ID to the URL
 * - Automatically syncs selected candidate IDs with the backend
 */
export const useSearchCandidateProfileSelected = ({
  sessionId: initialSessionId,
  tableRef
}: UseSearchCandidateProfileSelectedProps) => {
  const organization = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = initialSessionId || searchParams.get(SESSION_ID_PARAM);
  const { showToast } = useToast();

  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const hasCreatedSession = useRef(false);
  const hasInitializedSelection = useRef(false);
  const isSavingRef = useRef(false);
  // Latest selection requested while a save is already running. Coalesced and
  // flushed when the in-flight save finishes, so a click during a save isn't
  // dropped.
  const pendingSelectionRef = useRef<string[] | null>(null);
  const takePendingSelection = useCallback(() => {
    const pending = pendingSelectionRef.current;
    pendingSelectionRef.current = null;
    return pending;
  }, []);
  // Mirrors `selectedItems` so background saves read the latest committed items
  // across coalesced flushes rather than a stale render closure.
  const selectedItemsRef = useRef<SelectedItem[]>([]);
  const commitSelectedItems = useCallback((items: SelectedItem[]) => {
    selectedItemsRef.current = items;
    setSelectedItems(items);
  }, []);

  const [createSession] = useCreateSearchCandidateProfileSelectedMutation();
  const [createItem] = useCreateSearchCandidateProfileSelectedItemMutation();
  const [deleteItem] = useDeleteSearchCandidateProfileSelectedItemMutation();

  const { data } = useGetSearchCandidateProfileSelectedQuery({
    variables: { id: sessionId || '' },
    skip: !sessionId,
    fetchPolicy: 'network-only'
  });

  const { data: itemsData } = useGetSearchCandidateProfileSelectedItemsQuery({
    variables: {
      searchCandidateProfileSelectedId: sessionId || '',
      limit: 1000
    },
    skip: !sessionId,
    fetchPolicy: 'network-only'
  });

  // Load the selected items from the session and update the table
  useEffect(() => {
    if (!sessionId) {
      setIsInitialized(true);
      return;
    }

    if (!data || !itemsData) {
      return;
    }

    const selectedItemsData = extractQueryListItems(itemsData);

    // Only initialize the selection once to prevent duplicate selections
    if (hasInitializedSelection.current) {
      return;
    }

    const loadedItems: SelectedItem[] = selectedItemsData.map((item) => ({
      candidateProfileId: item.candidateProfileId,
      selectionId: item.id
    }));

    commitSelectedItems(loadedItems);
    setIsInitialized(true);
    hasInitializedSelection.current = true;

    const candidateIds = loadedItems.map((item) => item.candidateProfileId);
    if (candidateIds.length > 0) {
      tableRef?.current?.select(candidateIds);
    }
  }, [data, itemsData, sessionId]);

  const createNewSession = async (
    selectedIds: string[]
  ): Promise<string | undefined> => {
    try {
      setIsCreatingSession(true);

      const { data } = await createSession({
        variables: {
          input: {
            organizationId: organization.id
          }
        }
      });

      const newSessionId = data?.createSearchCandidateProfileSelected?.id;

      if (!newSessionId) {
        throw new Error('Failed to create session');
      }

      // Only once the session exists: set before the call, a failure left this
      // on with no session to go with it, and the guard below then refused to
      // try again, so no selection registered until the page was reloaded.
      hasCreatedSession.current = true;
      // This hook holds the selection for a session it made. Loading from the
      // server would read a partial list while the saves below run, and the
      // table would deselect the rows not saved yet.
      hasInitializedSelection.current = true;

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(SESSION_ID_PARAM, newSessionId);
      setSearchParams(newSearchParams, { replace: true });

      await saveSelectedIds(newSessionId, selectedIds);

      return newSessionId;
    } catch (error) {
      showToast({
        type: ToastType.Error,
        title: 'Failed to save selection',
        message: 'Unable to create selection session. Please try again.'
      });
      return undefined;
    } finally {
      setIsCreatingSession(false);
    }
  };

  const saveSelectedIds = async (
    sessionIdToUse: string,
    newSelectedIds: string[]
  ) => {
    try {
      setIsSavingSelection(true);
      const currentItems = selectedItemsRef.current;
      const currentIds = new Set(
        currentItems.map((item) => item.candidateProfileId)
      );
      const newIds = new Set(newSelectedIds);

      // Find items to create (newly selected)
      const idsToCreate = newSelectedIds.filter((id) => !currentIds.has(id));

      // Find items to delete (deselected)
      const itemsToDelete = currentItems.filter(
        (item) => !newIds.has(item.candidateProfileId)
      );

      // Delete deselected items
      await Promise.all(
        itemsToDelete.map(async (item) => {
          await deleteItem({
            variables: {
              input: {
                id: item.selectionId
              }
            }
          });
        })
      );

      // Create new items and get their real IDs from backend
      const createdItems = await Promise.all(
        idsToCreate.map(async (candidateProfileId) => {
          const result = await createItem({
            variables: {
              input: {
                searchCandidateProfileSelectedId: sessionIdToUse,
                candidateProfileId,
                organizationId: organization.id
              }
            }
          });
          const realId =
            result.data?.createSearchCandidateProfileSelectedItem?.id;
          return {
            candidateProfileId,
            selectionId: realId || `temp-${candidateProfileId}`
          };
        })
      );

      // Update state after all operations complete
      const remainingItems = currentItems.filter((item) =>
        newIds.has(item.candidateProfileId)
      );
      commitSelectedItems([...remainingItems, ...createdItems]);
    } catch (error) {
      showToast({
        type: ToastType.Error,
        title: 'Failed to update selection',
        message: 'Unable to save your selection changes. Please try again.'
      });
    } finally {
      setIsSavingSelection(false);
    }
  };

  const handleSelectionChange = useCallback(
    async (newSelectedIds: string[]) => {
      // A save is already running: remember only the latest requested selection
      // and let the in-flight save flush it when it finishes, instead of
      // dropping it (which left clicks made during a save round-trip silently
      // unregistered, hiding the selection bar).
      if (isSavingRef.current) {
        pendingSelectionRef.current = newSelectedIds;
        return;
      }

      if (newSelectedIds.length === 0 && !sessionId) {
        return;
      }

      isSavingRef.current = true;
      try {
        // Save the selection, threading the session id across coalesced flushes
        // so a change queued during session creation targets the new session.
        let currentSessionId = sessionId;
        await runCoalesced(
          newSelectedIds,
          takePendingSelection,
          async (ids) => {
            if (currentSessionId) {
              await saveSelectedIds(currentSessionId, ids);
            } else if (ids.length > 0 && !hasCreatedSession.current) {
              currentSessionId = (await createNewSession(ids)) ?? null;
            }
          }
        );
      } finally {
        isSavingRef.current = false;
      }
    },
    [sessionId, searchParams, takePendingSelection]
  );

  return {
    selectedCandidateIds: selectedItems.map((item) => item.candidateProfileId),
    handleSelectionChange,
    sessionId,
    isInitialized,
    isCreatingSession,
    isSavingSelection
  };
};
